import SendIcon from "@mui/icons-material/SendOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import { type ChangeEvent, type FC, type FormEvent, type KeyboardEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { SAFE_AREA_BOTTOM } from "src/components/AppShell/layout";

const MAX_ROWS = 6;

interface AdvisorComposerProps {
	busy: boolean;
	onSend: (text: string) => void;
	/** `card`: the desktop column's integrated composer; `pill`: the mobile one-line composer. */
	variant: "card" | "pill";
}

/** Figma "Sticky footer": Enter sends, Shift+Enter adds a line. */
export const AdvisorComposer: FC<AdvisorComposerProps> = ({ busy, onSend, variant }) => {
	const { t } = useTranslation();
	const [draft, setDraft] = useState("");
	const canSend = !busy && draft.trim().length > 0;

	const submit = useCallback(() => {
		if (!canSend) return;
		onSend(draft);
		setDraft("");
	}, [canSend, draft, onSend]);
	const handleSubmit = useCallback(
		(event: FormEvent) => {
			event.preventDefault();
			submit();
		},
		[submit]
	);
	const handleKeyDown = useCallback(
		(event: KeyboardEvent) => {
			if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
			event.preventDefault();
			submit();
		},
		[submit]
	);
	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDraft(event.target.value),
		[]
	);

	const pill = variant === "pill";
	const sendSize = pill ? 34 : 36;

	return (
		<Box
			sx={{
				flexShrink: 0,
				bgcolor: "background.paper",
				borderTop: 1,
				borderColor: "divider",
				pt: 1.5,
				px: 2.5,
				pb: `calc(${pill ? 18 : 30}px + ${SAFE_AREA_BOTTOM})`
			}}>
			<Box
				component="form"
				onSubmit={handleSubmit}
				sx={
					pill
						? {
								display: "flex",
								alignItems: "center",
								gap: 1.25,
								pl: 2,
								pr: 1,
								py: 1.25,
								borderRadius: "24px",
								border: "1.5px solid",
								borderColor: "brand.highlight"
							}
						: {
								display: "flex",
								flexDirection: "column",
								gap: 1.5,
								pt: 1.75,
								pl: 2,
								pr: 1.5,
								pb: 1.25,
								borderRadius: "16px",
								border: 1,
								borderColor: "brand.composerOutline",
								boxShadow: "0 4px 16px rgba(0, 0, 0, 0.12)"
							}
				}>
				<InputBase
					multiline
					maxRows={MAX_ROWS}
					value={draft}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					placeholder={t("advisor.placeholder")}
					inputProps={{ "aria-label": t("advisor.placeholder") }}
					sx={{ flex: 1, fontSize: pill ? 16 : 15, p: 0 }}
				/>
				<IconButton
					type="submit"
					disabled={!canSend}
					aria-label={t("advisor.send")}
					sx={{
						alignSelf: pill ? "center" : "flex-end",
						width: sendSize,
						height: sendSize,
						bgcolor: "primary.main",
						color: "primary.contrastText",
						"&:hover": { bgcolor: "primary.dark" },
						"&.Mui-disabled": { bgcolor: "primary.main", color: "primary.contrastText", opacity: 0.4 }
					}}>
					<SendIcon sx={{ fontSize: 16 }} />
				</IconButton>
			</Box>
		</Box>
	);
};
